import React, { useState, useEffect } from "react";
import { NavLink, useParams } from "react-router-dom";
import {
  Box,
  Text,
  Flex,
  Tab,
  Tabs,
  TabList,
  TabPanels,
  TabPanel,
  Tag,
  TagLabel,
  Badge,
  Tooltip,
  IconButton,
  HStack,
  Menu,
  MenuList,
  MenuItem,
  MenuButton,
  Button
} from "@chakra-ui/react";
import { useLocation } from "react-router-dom";
import { useAppContext } from "../../../contexts/AppContext";
import LibraryTable from "../LibraryTable";
import InvestmentsTable from "../InvestmentsTable";
import InvestorsTable from "../InvestorsTable";
import SingleFundOverview from "./Overview";
import { Earth, CalendarCheck2, FileDigit, ArrowLeft, Plus } from "lucide-react";
import useCustomTheme from "../../../hooks/useCustomTheme";
import ReportsTable from "./ReportsTable";
import NavTable from "./NAV/NavTable";
import SingleFundTasks from "./SingleFundTasks";

const SingleFund = ({ openTaskDrawer, reportManager }) => {
  const { fundName } = useParams();
  const { data, tasks } = useAppContext();
  const location = useLocation();
  const { theme } = useCustomTheme();
  const formattedFundName = fundName.replace(/-/g, " ");
  const fund = data.find((fund) => fund.fundName === formattedFundName);
  const relatedTasks = tasks.filter((task) => task.fund === fund.fundName);
  const remainingRelatedTasks = relatedTasks.filter(
    (task) => !task.completed
  ).length;

  // Detect active tab based on hash
  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    if (location.hash === "#investors") {
      setActiveTab(3);
    }
    if (location.hash === "#investments") {
      setActiveTab(4);
    }
  }, [location.hash]);

  if (!fund) return <Text>Select a fund to view details.</Text>;

  return (
    <Flex gap={8} direction="column">
    <Flex justify={{ lg: "space-between", base: "flex-start" }} w={"full"} align={"center"} direction={{ lg: "row", base: "column" }} gap={4}>
        <Flex align={"center"} gap={4}>
          <IconButton
            aria-label="Back"
            variant={"ghost"}
            icon={<ArrowLeft size={32} />}
            as={NavLink}
            to={`/funds/active`}
          
            borderRadius={"full"}
          />
          <h1>{fund.fundName}</h1>
          <Tag gap={2} size={"lg"}>
            <Box
              as="span"
              bg={fund.investmentStrategy.color}
              h="8px"
              w="8px"
              borderRadius={"full"}
            />
            <TagLabel>{fund.investmentStrategy.name}</TagLabel>
          </Tag>
        </Flex>


      <HStack spacing={8}>

      <Flex direction={"row"} gap={8}>
          <Tooltip label="Geography">
            <Flex
              gap={2}
              direction={"row"}
              alignItems={"center"}
              display={"flex"}
              fontWeight={500}>
              <Earth size={16} color={theme.colors.grey[500]} />
              {fund.factSheet.overview.geography}
            </Flex>
          </Tooltip>

          <Tooltip label="Vintage Year">
            <Flex
              gap={2}
              direction={"row"}
              alignItems={"center"}
              display={"flex"}
              fontWeight={500}>
              <CalendarCheck2 size={16} color={theme.colors.grey[500]} />
              {fund.factSheet.overview.vintageYear}
            </Flex>
          </Tooltip>

          <Tooltip label="Asset Number">
            <Flex
              gap={2}
              direction={"row"}
              alignItems={"center"}
              display={"flex"}
              fontWeight={500}>
              <FileDigit size={16} color={theme.colors.grey[500]} />
              {fund.factSheet.overview.assetNumber}
            </Flex>
          </Tooltip>
        </Flex>

      <Menu placement="auto">
            <MenuButton
              as={Button}
              colorScheme="brand"
              size={"sm"}
              leftIcon={<Plus size={20} />}
            >Add</MenuButton>
            <MenuList>
              <MenuItem>Add report</MenuItem>
              <MenuItem>Add investor</MenuItem>
              <MenuItem>Add investment</MenuItem>
              <MenuItem>Add NAV</MenuItem>
              <MenuItem>Add document</MenuItem>
            </MenuList>
          </Menu>


      </HStack>
        
      </Flex>

      <Tabs
        variant="line"
        colorScheme="brand"
        index={activeTab}
        onChange={(index) => setActiveTab(index)}>
        <TabList mb={8}>
          <Tab>Overview</Tab>
          <Tab>Reports</Tab>
          <Tab>
            Tasks{" "}
            {remainingRelatedTasks > 0 && (
              <Badge colorScheme="error" variant={"solid"} ml={2}>
                {remainingRelatedTasks}
              </Badge>
            )}
          </Tab>
          <Tab>Investors</Tab>
          <Tab>Investments</Tab>
          <Tab>NAV</Tab>
          <Tab>Library</Tab>
        </TabList>

        <TabPanels>
          <TabPanel p={0}>
            <SingleFundOverview />
          </TabPanel>

          <TabPanel p={0}>
            <ReportsTable fund={fund} reportManager={reportManager} />
          </TabPanel>

          <TabPanel p={0}>
            
            <SingleFundTasks openTaskDrawer={openTaskDrawer} fund={fund} />

          </TabPanel>
          <TabPanel p={0}>
            <InvestorsTable fund={fund} />
          </TabPanel>

          <TabPanel p={0}>
            <InvestmentsTable fund={fund} />
          </TabPanel>


          <TabPanel p={0}>
            <NavTable fund={fund} />
          </TabPanel>

          <TabPanel p={0}>
            <LibraryTable fund={fund} />
          </TabPanel>
        </TabPanels>
      </Tabs>
    </Flex>
  );
};

export default SingleFund;
